import { Fragment, ReactNode, FormEvent } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void> | void;
  title: string;
  description?: string;
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  /** disables the submit button when true (e.g. form not yet valid) */
  disabled?: boolean;
  /** modal width — defaults to max-w-lg */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * A window with nothing to save — a list to read and act on row by row.
   * It shows ONE control, "Close" (Deep-test EXT-70: the Watchlist showed both
   * Cancel and a Close that was really a submit, two buttons doing one thing).
   */
  closeOnly?: boolean;
  /**
   * Why the submit is off, in words beside it (FM Technician brief: every
   * disabled action says why). Shown only while `disabled`.
   */
  disabledReason?: string;
  /** Extra classes on the panel — `tech` gives a technician's 48 px
   *  buttons and 14 px text to everything inside, the footer included. */
  panelClassName?: string;
}

const SIZE_CLASS = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/**
 * Reusable form modal — Headless UI Dialog gives us:
 *   - Escape key closes
 *   - backdrop click closes
 *   - focus trapped inside
 *   - aria-* attributes
 *
 * Usage:
 *   <FormModal isOpen={open} onClose={...} onSubmit={async () => { ... }} title="Add Lease">
 *     <input ... />
 *   </FormModal>
 */
export default function FormModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  description,
  children,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isLoading = false,
  disabled = false,
  size = 'md',
  closeOnly = false,
  disabledReason,
  panelClassName = '',
}: FormModalProps) {
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading || disabled) return;
    // Deep-test ENC-025: a date field holding text it could not read (marked
    // by PatternDateInput) stops the save, naming the field. Without this a
    // form read only the field's VALUE — still "" — and saved "no due date"
    // for "31/02/2027" under a field that said the date was wrong.
    const unread = e.currentTarget.querySelector<HTMLInputElement>('[data-date-invalid="true"]');
    if (unread) {
      const label = (unread.id && e.currentTarget.querySelector(`label[for="${CSS.escape(unread.id)}"]`)?.textContent)
        || unread.getAttribute('aria-label') || 'Date';
      toast.error(`${label.replace(/\*/g, '').trim()}: ${unread.dataset.dateError ?? 'enter a valid date'}`);
      unread.focus();
      return;
    }
    try {
      await onSubmit();
    } catch (err) {
      /*
       * The failure has ALREADY been reported by the time it gets here. Every
       * caller runs its save through a mutation whose onError puts the message
       * on the field and leaves the modal open — React Query calls onError
       * before mutateAsync rejects, so the field-level routing is untouched by
       * this catch. What was left over was the rejection itself: React does not
       * await this handler, so it escaped as an unhandled rejection.
       *
       * Nothing about that was VISIBLE — the modal stays open, the error shows,
       * a second attempt works. The cost was elsewhere: the shipped bundle
       * installs Sentry's onunhandledrejection handler, so with a DSN set at
       * build time every duplicate-code 409 and validation 422 — routine user
       * outcomes, not crashes — was filed as an unhandled error.
       *
       * Logged rather than swallowed. A genuine bug inside a submit handler (a
       * TypeError, an undefined read) also lands here, and silence would make
       * it strictly harder to find than it is today. console.error is not wired
       * to Sentry: captureConsoleIntegration is not among the integrations.
       */
      // eslint-disable-next-line no-console
      console.error('[FormModal] submit failed:', err);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={isLoading ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-surface-inverse/40 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full ${SIZE_CLASS[size]} transform overflow-hidden rounded-2xl bg-surface shadow-xl transition-all ${panelClassName}`.trimEnd()}>
                <form onSubmit={handleSubmit}>
                  <div className="px-6 py-5">
                    <Dialog.Title as="h3" className="text-base font-semibold text-content">
                      {title}
                    </Dialog.Title>
                    {description && (
                      <p className="mt-1 text-xs text-content-muted">{description}</p>
                    )}
                    <div className="mt-5 space-y-4">{children}</div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-3 border-t border-line-subtle bg-surface-subtle px-6 py-4">
                    {disabled && !isLoading && !closeOnly && disabledReason && (
                      <p className="mr-auto text-sm text-content-muted" data-testid="form-modal-why">{disabledReason}</p>
                    )}
                    {closeOnly ? (
                      <button type="button" onClick={onClose} className="btn-primary text-sm">
                        Close
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={onClose}
                          disabled={isLoading}
                          className="btn-secondary text-sm"
                        >
                          {cancelLabel}
                        </button>
                        <button
                          type="submit"
                          disabled={isLoading || disabled}
                          className="btn-primary text-sm disabled:opacity-50"
                        >
                          {isLoading ? 'Working…' : submitLabel}
                        </button>
                      </>
                    )}
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
